import { useFormik } from "formik";
import { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import UserAddressService from "../../services/useraddress/useraddress.service";
import { CountryModel } from "../../models/country-model";
import { StateModel } from "../../models/state-model";
import { DistrictModel } from "../../models/district-model";
import { SubDistrictModel } from "../../models/subDistrict-model";

import { Dialog } from "primereact/dialog";
import AddCountryPage from "../admin/AddCountry";

const AddingNewArea = () => {
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [states, setStates] = useState<StateModel[]>([]);
  const [districts, setDistricts] = useState<DistrictModel[]>([]);
  const [subDistricts, setSubDistricts] = useState<SubDistrictModel[]>([]);
  const toast = useRef<Toast>(null);
  const [showAddCountry, setShowAddCountry] = useState(false);
  const [showAddState, setShowAddState] = useState(false);
  const [showAddDistrict, setShowAddDistrict] = useState(false);
  const [showAddSubDistrict, setShowAddSubDistrict] = useState(false);
  const [newStateName, setNewStateName] = useState("");
  const [newDistrictName, setNewDistrictName] = useState("");
  const [newSubDistrictName, setNewSubDistrictName] = useState("");

  useEffect(() => {
    UserAddressService.getAllCountryNames().then((countries: CountryModel[]) => {
      setCountries(countries);
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      areaName: "",
      pincode: "",
      countryId: "",  
      stateId: "",
      districtId: "",
      subDistrictId: "",
    },
    validate: (data: any) => {
      let errors: any = {};
      if (!data.areaName) {
        errors.areaName = "Area Name is required";
      }
      if (!data.pincode) {
        errors.pincode = "Pincode is required";
      } else if(isNaN(Number(data.pincode))){
        errors.pincode = "Pincode must be numeric";
      } else if (data.pincode.length < 4 || data.pincode.length > 6) {
        errors.pincode = "Pincode must be 4 to 6 digits";
      }    

      if (!data.countryId) {  
        errors.countryId = "Please select a country";
      }
      if (!data.stateId) {
        errors.stateId = "Please select a state";
      }
      if (!data.districtId) {
        errors.districtId = "Please select a district";
      }
      if (!data.subDistrictId) {
        errors.subDistrictId = "Please select a subdistrict";
      }
     
      return errors;
    },
    onSubmit: async (data: any, { resetForm }) => {
      const areaData = {
        areaName: data.areaName,
        pinCode: parseInt(data.pincode),
        subDistrictId: data.subDistrictId,
      };
      try {
        const response = await UserAddressService.addArea(areaData);
        if (response && response.message) {
          toast.current?.show({ severity: 'success', summary: response.message });
          resetForm();
          setStates([]);
          setDistricts([]);
          setSubDistricts([]);
          
        } else {
          toast.current?.show({ severity: 'error', summary: 'Area/Pincode Already Exist' });
        }
      } catch (error) {
        toast.current?.show({ severity: 'error', summary: 'Error creating area' });
      }
    },
  });

  const handleCountryChange = (e: any) => {
    const countryId = e.value;
    formik.setFieldValue("countryId", countryId); 
    setStates([]);
    UserAddressService.getStatesByCountryID(countryId).then((states: StateModel[]) => setStates(states));
  };

  const handleStateChange = (e: any) => {
    const stateId = e.value;
    formik.setFieldValue("stateId", stateId);
    setDistricts([]);
    UserAddressService.getDistrictsByStateId(stateId).then((districts: DistrictModel[]) => setDistricts(districts));
  };

  const handleDistrictChange = (e: any) => {
    const districtId = e.value;
    formik.setFieldValue("districtId", districtId);
    setSubDistricts([]);
    UserAddressService.getSubDistrictsByStateId(districtId).then((subDistricts: SubDistrictModel[]) => setSubDistricts(subDistricts));
  };

  //------------------------------------------------------------------------
  const handleAddNewState = async () => {
    if (!newStateName.trim() || !formik.values.countryId) {
      toast.current?.show({ severity: 'error', summary: 'State name and country selection are required' });
      return;
    }
 
    try {
      const response = await UserAddressService.addNewState({
        stateName: newStateName,
        countryId: formik.values.countryId
      });
     
      if (response && response.stateId) {
        toast.current?.show({ severity: 'success', summary: 'State added successfully' });
        setShowAddState(false);
        setNewStateName("");
        UserAddressService.getStatesByCountryID(formik.values.countryId).then((states: StateModel[]) => {
          setStates(states);
          formik.setFieldValue("state", response.stateId);
        });
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error adding new state' });
    }
  };
 
  const handleAddNewDistrict = async () => {
    if (!newDistrictName.trim() || !formik.values.stateId) {
      toast.current?.show({ severity: 'error', summary: 'District name and state selection are required' });
      return;
    }
 
    try {
      const response = await UserAddressService.addNewDistrict({
        districtName: newDistrictName,
        stateId: formik.values.stateId
      });
     
      if (response && response.districtId) {
        toast.current?.show({ severity: 'success', summary: 'District added successfully' });
        setShowAddDistrict(false);
        setNewDistrictName("");
        UserAddressService.getDistrictsByStateId(formik.values.stateId).then((districts: DistrictModel[]) => {
          setDistricts(districts);
          formik.setFieldValue("district", response.districtId);
        });
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error adding new district' });
    }
  };
 
  const handleAddNewSubDistrict = async () => {
    if (!newSubDistrictName.trim() || !formik.values.districtId) {
      toast.current?.show({ severity: 'error', summary: 'Subdistrict name and district selection are required' });
      return;
    }
 
    try {
      const response = await UserAddressService.addNewSubDistrict({
        subDistrictName: newSubDistrictName,
        districtId: formik.values.districtId
      });
     
      if (response && response.subDistrictId) {
        toast.current?.show({ severity: 'success', summary: 'Subdistrict added successfully' });
        setShowAddSubDistrict(false);
        setNewSubDistrictName("");
        UserAddressService.getSubDistrictsByStateId(formik.values.districtId).then((subDistricts: SubDistrictModel[]) => {
          setSubDistricts(subDistricts);
          formik.setFieldValue("subDistricts", response.subDistrictId);
        });      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error adding new subdistrict' });
    }
  };
 
  return (
    <>
      <Toast ref={toast} />
      <div className="col-12">
        <h5 className="border-round order-top-color p-2">Add Pincode/Area</h5>
      </div>

      <div className="flex flex-column md:flex-row">
        <div className="col-12 mt-2">
          <form onSubmit={formik.handleSubmit} className="flex flex-column">
            <div className="p-fluid formgrid grid">
              {/* Country Dropdown */}
              <div className="grid col-12">
                <div className="field col-12 md:col-9 bold-text">
                  <label htmlFor="countryId"> Country :</label>
                  <div className="flex justify-content-between">
                    <Dropdown
                      id="countryId"
                      name="countryId" 
                      value={formik.values.countryId} 
                      onChange={handleCountryChange}
                      options={countries}
                      optionLabel="countryName"
                      optionValue="countryId"  
                      placeholder="Select a Country"
                      className={formik.errors.countryId ? 'p-invalid' : ''}
                    />
                    <Button
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-success mb-3"
                    onClick={() => setShowAddCountry(true)}
                  />

                  </div>
                  {formik.errors.countryId && typeof formik.errors.countryId === 'string' && (
                    <small className="p-error">{formik.errors.countryId}</small>
                  )}
                </div>
              </div>

              <div className="grid col-12">
                <div className="field col-12 md:col-7 mt-2 bold-text">
                  <label htmlFor="state">State :</label>
                  <Dropdown
                    id="stateId"
                    name="stateId"
                    value={formik.values.stateId}
                    onChange={handleStateChange}
                    options={states}
                    optionLabel="stateName"
                    optionValue="stateId"
                    placeholder="Select a State"
                    className={formik.errors.stateId ? 'p-invalid' : ''}
                  />
                  <Button
                  icon="pi pi-plus"
                  className="p-button-rounded p-button-success mb-3"
                  onClick={() => setShowAddState(true)}
                  disabled={!formik.values.countryId}
                />

                  {formik.errors.stateId && typeof formik.errors.stateId === 'string' && (
                    <small className="p-error">{formik.errors.stateId}</small>
                  )}
                </div>
              </div>


              <div className="grid col-12">
                <div className="field col-12 md:col-7 mt-2 bold-text">
                  <label htmlFor="district">District :</label>
                  <Dropdown
                    id="districtId"
                    name="districtId"
                    value={formik.values.districtId}
                    onChange={handleDistrictChange}
                    options={districts}
                    optionLabel="districtName"
                    optionValue="districtId"
                    placeholder="Select a District"
                    className={formik.errors.districtId ? 'p-invalid' : ''}
                  />
                  <Button
                  icon="pi pi-plus"
                  className="p-button-rounded p-button-success mb-3"
                  onClick={() => setShowAddDistrict(true)}
                  disabled={!formik.values.stateId}
                />

                  {formik.errors.districtId && typeof formik.errors.districtId === 'string' && (
                    <small className="p-error">{formik.errors.districtId}</small>
                  )}
                </div>
              </div>

              <div className="grid col-12">
                <div className="field col-12 md:col-7 mt-2 bold-text">
                  <label htmlFor="subDistrict">Sub District :</label>
                  <Dropdown
                    id="subDistrictId"
                    name="subDistrictId"
                    value={formik.values.subDistrictId}
                    onChange={formik.handleChange}
                    options={subDistricts}
                    optionLabel="subDistrictName"
                    optionValue="subDistrictId"
                    placeholder="Select a Sub District"
                    className={formik.errors.subDistrictId ? 'p-invalid' : ''}
                  />
                  <Button
                  icon="pi pi-plus"
                  className="p-button-rounded p-button-success mb-3"
                  onClick={() => setShowAddSubDistrict(true)}
                  disabled={!formik.values.districtId}
                />

                  {formik.errors.subDistrictId && typeof formik.errors.subDistrictId === 'string' && (
                    <small className="p-error">{formik.errors.subDistrictId}</small>
                  )}
                </div>
              </div>

              <div className="grid col-12">
                <div className="field col-12 md:col-7 mt-2 bold-text">
                  <label htmlFor="pincode">Pincode :</label>
                  <InputText
                    keyfilter="num" 
                    maxLength={6}
                    id="pincode"
                    name="pincode"
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    placeholder="Enter Pincode"
                    className={formik.errors.pincode ? 'p-invalid' : ''}
                  />
                  {formik.errors.pincode && typeof formik.errors.pincode === 'string' && (
                    <small className="p-error">{formik.errors.pincode}</small>
                  )}
                </div>
              </div>

              <div className="grid col-12">
                <div className="field col-12 md:col-7 mt-2 bold-text">
                  <label htmlFor="areaName">Area Name :</label>
                  <InputText
                    id="areaName"
                    name="areaName"
                    value={formik.values.areaName}
                    onChange={formik.handleChange}
                    placeholder="Enter Area Name"
                    className={formik.errors.areaName ? 'p-invalid' : ''}
                  />
                  {formik.errors.areaName && typeof formik.errors.areaName === 'string' && (
                    <small className="p-error">{formik.errors.areaName}</small>
                  )}
                </div>
              </div>

              <div className="col-12 flex justify-content-between">
                <Button
                  type="submit"
                  label="Add Area"
                  className="bg-primary w-7rem mt-2"
                  severity="success"
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      <Dialog
        visible={showAddCountry}
        onHide={() => setShowAddCountry(false)}
        header="Add New Country"
        className="w-6"
      >
        <AddCountryPage
          showAddCountryModal={showAddCountry}
          hideAddCountryModal={() => setShowAddCountry(false)}
          onSuccess={() => {
            setShowAddCountry(false);
            UserAddressService.getCountries().then((countries: CountryModel[]) => {
              setCountries(countries);
            });
          }}
        />
      </Dialog>
 
      <Dialog
        visible={showAddState}
        onHide={() => setShowAddState(false)}
        header="Add New State"
        className="w-6"
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="newStateName">State Name</label>
            <InputText
              id="newStateName"
              value={newStateName}
              onChange={(e) => setNewStateName(e.target.value)}
              placeholder="Enter state name"
            />
          </div>
          <div className="flex justify-content-end mt-3">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAddState(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleAddNewState}
              autoFocus
            />
          </div>
        </div>
      </Dialog>
 
      <Dialog
        visible={showAddDistrict}
        onHide={() => setShowAddDistrict(false)}
        header="Add New District"
        className="w-6"
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="newDistrictName">District Name</label>
            <InputText
              id="newDistrictName"
              value={newDistrictName}
              onChange={(e) => setNewDistrictName(e.target.value)}
              placeholder="Enter district name"
            />
          </div>
          <div className="flex justify-content-end mt-3">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAddDistrict(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleAddNewDistrict}
              autoFocus
            />
          </div>
        </div>
      </Dialog>
 
      <Dialog
        visible={showAddSubDistrict}
        onHide={() => setShowAddSubDistrict(false)}
        header="Add New Subdistrict"
        className="w-6"
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="newSubDistrictName">Subdistrict Name</label>
            <InputText
              id="newSubDistrictName"
              value={newSubDistrictName}
              onChange={(e) => setNewSubDistrictName(e.target.value)}
              placeholder="Enter subdistrict name"
            />
          </div>
          <div className="flex justify-content-end mt-3">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAddSubDistrict(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleAddNewSubDistrict}
              autoFocus
            />
          </div>
        </div>
      </Dialog>
 
    </>
  );
};

export default AddingNewArea;
